import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { API_ENDPOINTS } from '../config/api';

interface User {
  id: number;
  email: string;
  username: string;
  full_name?: string;
  subscription_plan?: string;
  email_verified?: boolean;
  is_active?: boolean;
  is_admin?: boolean;
  created_at: string;
  last_login?: string;
  has_seen_onboarding_video?: boolean;
}

interface LoginResult {
  redirect_to_verification?: boolean;
  verification_url?: string;
  access_token?: string;
  user?: User;
  message?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<LoginResult | void>;
  register: (email: string, username: string, password: string, full_name?: string, plan?: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setToken: (token: string | null) => void;
  setUser: (user: User | null) => void;
  markOnboardingVideoSeen: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Charger les données d'authentification depuis le localStorage au démarrage
  useEffect(() => {
    const loadAuthData = async () => {
      try {
        const storedToken = localStorage.getItem('access_token');
        const storedUser = localStorage.getItem('user_data');

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          
          // Vérifier si le token est toujours valide
          try {
            const response = await fetch(API_ENDPOINTS.ME, {
              headers: {
                'Authorization': `Bearer ${storedToken}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const userData = await response.json();
              setUser(userData);
              localStorage.setItem('user_data', JSON.stringify(userData));
            } else {
              // Token invalide, nettoyer le localStorage
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('user_data');
              setToken(null);
              setUser(null);
            }
          } catch (error) {
            console.error('Erreur lors de la vérification du token:', error);
            // En cas d'erreur, nettoyer le localStorage
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user_data');
            setToken(null);
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données d\'authentification:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAuthData();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Messages d'erreur spécifiques pour la connexion
        let errorMessage = 'Erreur de connexion';
        
        if (response.status === 401) {
          if (errorData.detail) {
            if (errorData.detail.includes('Email ou mot de passe incorrect')) {
              errorMessage = '❌ Email ou mot de passe incorrect';
            } else if (errorData.detail.includes('Utilisateur non trouvé')) {
              errorMessage = '❌ Aucun compte trouvé avec cet email';
            } else if (errorData.detail.includes('Compte désactivé')) {
              errorMessage = '❌ Votre compte a été désactivé';
            } else {
              errorMessage = `❌ ${errorData.detail}`;
            }
          } else {
            errorMessage = '❌ Email ou mot de passe incorrect';
          }
        } else if (response.status === 422) {
          errorMessage = '❌ Données de connexion invalides';
        } else if (response.status === 429) {
          errorMessage = '❌ Trop de tentatives de connexion. Réessayez plus tard';
        } else if (response.status >= 500) {
          errorMessage = '❌ Erreur serveur. Réessayez plus tard';
        } else if (errorData.detail) {
          errorMessage = `❌ ${errorData.detail}`;
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Vérifier si c'est une redirection vers la vérification d'email
      if (data.redirect_to_verification) {
        // Stocker le token temporaire
        localStorage.setItem('access_token', data.access_token);
        setToken(data.access_token);
        
        // Récupérer les données utilisateur même en cas de redirection
        try {
          const userResponse = await fetch(API_ENDPOINTS.ME, {
            headers: {
              'Authorization': `Bearer ${data.access_token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (userResponse.ok) {
            const userData = await userResponse.json();
            setUser(userData);
            localStorage.setItem('user_data', JSON.stringify(userData));
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des données utilisateur:', error);
        }
        
        // Retourner les données pour que le composant puisse gérer la redirection
        console.log('🔍 useAuth: Redirection vers vérification d\'email détectée');
        console.log('🔍 useAuth: verification_url:', data.verification_url);
        console.log('🔍 useAuth: redirect_to_verification:', data.redirect_to_verification);
        console.log('🔍 useAuth: message:', data.message);
        return data; // Retourner les données au lieu de rediriger directement
      }
      
      // Stocker les tokens
      localStorage.setItem('access_token', data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      
      setToken(data.access_token);

      // Les données utilisateur sont déjà dans la réponse PostgreSQL
      if (data.user) {
        setUser(data.user);
        localStorage.setItem('user_data', JSON.stringify(data.user));
      } else {
        // Fallback : récupérer les informations utilisateur
        const userResponse = await fetch(API_ENDPOINTS.ME, {
          headers: {
            'Authorization': `Bearer ${data.access_token}`,
            'Content-Type': 'application/json'
          }
        });

        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData);
          localStorage.setItem('user_data', JSON.stringify(userData));
        }
      }
      
      console.log('🔍 useAuth: Connexion normale réussie');
      console.log('🔍 useAuth: redirect_to_verification:', data.redirect_to_verification);
      return data; // Retourner les données de connexion
    } catch (error) {
      console.error('Erreur de connexion:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, username: string, password: string, full_name?: string, plan: string = 'free') => {
    try {
      setIsLoading(true);
      
      const response = await fetch(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, username, password, confirm_password: password, full_name, plan }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Messages d'erreur spécifiques pour l'inscription
        let errorMessage = 'Erreur d\'inscription';
        
        if (response.status === 400) {
          if (errorData.detail) {
            if (Array.isArray(errorData.detail)) {
              // Erreurs de validation Pydantic
              const validationErrors = errorData.detail.map((err: any) => {
                if (err.loc && err.loc.length > 1) {
                  const field = err.loc[1];
                  switch (field) {
                    case 'email':
                      return 'Format d\'email invalide';
                    case 'password':
                      return 'Mot de passe trop faible (minimum 8 caractères)';
                    case 'username':
                      return 'Nom d\'utilisateur invalide';
                    case 'confirm_password':
                      return 'Les mots de passe ne correspondent pas';
                    default:
                      return err.msg;
                  }
                }
                return err.msg;
              });
              errorMessage = `❌ ${validationErrors.join(', ')}`;
            } else if (errorData.detail.includes('Email déjà utilisé')) {
              errorMessage = '❌ Cet email est déjà utilisé';
            } else if (errorData.detail.includes('Nom d\'utilisateur déjà utilisé')) {
              errorMessage = '❌ Ce nom d\'utilisateur est déjà utilisé';
            } else if (errorData.detail.includes('Mot de passe trop faible')) {
              errorMessage = '❌ Mot de passe trop faible (minimum 8 caractères)';
            } else {
              errorMessage = `❌ ${errorData.detail}`;
            }
          } else {
            errorMessage = '❌ Données d\'inscription invalides';
          }
        } else if (response.status === 409) {
          errorMessage = '❌ Email ou nom d\'utilisateur déjà utilisé';
        } else if (response.status === 422) {
          errorMessage = '❌ Données d\'inscription invalides';
        } else if (response.status >= 500) {
          errorMessage = '❌ Erreur serveur. Réessayez plus tard';
        } else if (errorData.detail) {
          errorMessage = `❌ ${errorData.detail}`;
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Ne pas stocker les tokens immédiatement - attendre la vérification email
      // Les tokens seront stockés après la vérification email
      console.log('Inscription réussie, en attente de vérification email');
      
      // Retourner les données pour que le composant puisse gérer la redirection
      return data;
    } catch (error) {
      console.error('Erreur d\'inscription:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Appeler l'endpoint de déconnexion si un token existe
      if (token) {
        await fetch(API_ENDPOINTS.LOGOUT, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      // Nettoyer le state et le localStorage
      setToken(null);
      setUser(null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_data');
    }
  };

  const refreshToken = async () => {
    try {
      const storedRefreshToken = localStorage.getItem('refresh_token');
      
      if (!storedRefreshToken) {
        throw new Error('Aucun token de rafraîchissement disponible');
      }

      const response = await fetch(API_ENDPOINTS.REFRESH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: storedRefreshToken }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors du rafraîchissement du token');
      }

      const data = await response.json();
      
      // Mettre à jour le token
      localStorage.setItem('access_token', data.access_token);
      setToken(data.access_token);
    } catch (error) {
      console.error('Erreur lors du rafraîchissement du token:', error);
      // En cas d'erreur, déconnecter l'utilisateur
      logout();
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const currentToken = token || localStorage.getItem('access_token');
      
      if (!currentToken) {
        throw new Error('Aucun token disponible');
      }

      const response = await fetch(API_ENDPOINTS.ME, {
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du rafraîchissement des données utilisateur');
      }

      const userData = await response.json();
      
      setUser(userData);
      localStorage.setItem('user_data', JSON.stringify(userData));
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des données utilisateur:', error);
      throw error;
    }
  };

  const markOnboardingVideoSeen = useCallback(async () => {
    try {
      const currentToken = token || localStorage.getItem('access_token');

      if (!currentToken) {
        throw new Error('Aucun token disponible');
      }

      const response = await fetch(API_ENDPOINTS.ONBOARDING_VIDEO_COMPLETE, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Impossible de mettre à jour le statut de la vidéo onboarding');
      }

      const data = await response.json();

      if (data?.user) {
        setUser(data.user);
        localStorage.setItem('user_data', JSON.stringify(data.user));
      } else if (user) {
        const updatedUser = { ...user, has_seen_onboarding_video: true };
        setUser(updatedUser);
        localStorage.setItem('user_data', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut vidéo onboarding:', error);
      throw error;
    }
  }, [token, user]);

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    login,
    register,
    logout,
    refreshToken,
    refreshUser,
    setToken,
    setUser,
    markOnboardingVideoSeen,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
}
